import { Autocomplete, TextField, Typography } from '@mui/material';
import { WidgetProps } from '@rjsf/utils';

import { WorkflowNodeType } from 'api/model';
import { getNodeConfig, getNodeColorByGroup } from 'api/workflows-steps/formSchema';
import { FormBlockContext } from './FormBlock';
import { useMemo } from 'react';

type InputColumn = {
  id: string;
  name: string;
  label: string;
  type: WorkflowNodeType;
  allowed: boolean;
  columns: string[];
};

const nodeIconStyle = {
  backgroundColor: '',
  borderRadius: '5px',
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: 16,
};

const SourceStepWidget = function (props: WidgetProps<any, any, FormBlockContext>) {
  const { formContext, value, onChange } = props;

  const { required_fields, text } = props.options as {
    input_columns: [];
    required_fields: string[];
    text: string;
    error_text: string;
  };

  const inputColumns: InputColumn[] = useMemo(() => {
    if (!formContext?.inputs?.length) return [];

    return formContext.inputs.map((input) => ({
      id: input.data.id,
      name: input.data.name,
      label: input.data.name,
      type: input.data.type as WorkflowNodeType,
      allowed: required_fields.every((field) => input.columns.includes(field)),
      columns: input.columns,
    }));
  }, [formContext?.inputs, required_fields]);

  return (
    <div className="flex flex-col m-0">
      {/* ComboBox */}
      <Autocomplete<InputColumn, false, false, false>
        disablePortal
        disabled={props.disabled}
        options={inputColumns}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option; // fallback
          }
          return option?.label || '';
        }}
        value={inputColumns.find((opt) => opt.id === value) ?? null}
        onChange={(_, newValue) => {
          onChange(newValue?.id ?? '');
        }}
        renderOption={(props, option) => {
          const nodeConfig = getNodeConfig(option.type);
          return (
            <li {...props}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                className={
                  option.allowed
                    ? 'text-green-600 font-semibold'
                    : option.columns.length == 0
                      ? 'text-zinc-600'
                      : 'text-red-500'
                }
              >
                {nodeConfig && (
                  <div style={{ ...nodeIconStyle, backgroundColor: getNodeColorByGroup(nodeConfig.group) }}>
                    {nodeConfig.icon}
                  </div>
                )}
                <span>{option.label} </span>
                {option.columns.length > 0 && (
                  <span className="font-semibold text-sm">["{option.columns.join('", "')}"]</span>
                )}
              </div>
            </li>
          );
        }}
        renderInput={(params) => {
          return (
            <TextField {...params} label={props.options.label} variant="outlined" InputLabelProps={{ shrink: true }} />
          );
        }}
        sx={{ mt: 1 }}
      />
      {text && (
        <Typography variant="body2" color="text.disabled" component={'pre'}>
          {text}
        </Typography>
      )}
    </div>
  );
};

export { SourceStepWidget };
